import { useMemo, useEffect } from "react";
import SearchableSelect, {
  SearchableOption,
} from "@/components/atoms/SearchableSelect";
import { useGetAvailableBranches } from "@/hooks/use-get-available-branches";
import { useOrdersStore } from "./use-orders-store";

export default function OrderBranchFilter() {
  const { availableBranches, isLoadingUser } = useGetAvailableBranches();
  const branchFilter = useOrdersStore((state) => state.branchFilter);
  const setBranchFilter = useOrdersStore((state) => state.setBranchFilter);
  const brandFilter = useOrdersStore((state) => state.brandFilter);

  // Filter branches based on selected brand
  const filteredBranches = useMemo(() => {
    if (brandFilter === "all") {
      return availableBranches;
    }
    return availableBranches.filter(
      (branch) => branch.brand_id === brandFilter,
    );
  }, [availableBranches, brandFilter]);

  // Reset branch filter when brand changes and current branch doesn't belong to new brand
  useEffect(() => {
    if (brandFilter !== "all" && branchFilter !== "all") {
      const currentBranch = availableBranches.find(
        (branch) => branch.id === branchFilter,
      );
      if (currentBranch && currentBranch.brand_id !== brandFilter) {
        setBranchFilter("all");
      }
    }
  }, [brandFilter, branchFilter, availableBranches, setBranchFilter]);

  // Convert filtered branches to SearchableSelect options
  const branchOptions: SearchableOption[] = useMemo(() => {
    return filteredBranches.map((branch) => ({
      value: branch.id,
      label: branch.name,
    }));
  }, [filteredBranches]);

  // Handle branch selection
  const handleBranchChange = (value: string | number | (string | number)[]) => {
    if (Array.isArray(value)) {
      // Handle multiple selection (not expected in this case, but for safety)
      return;
    }

    if (value === "all") {
      setBranchFilter("all");
    } else {
      setBranchFilter(Number(value));
    }
  };

  // Don't render if still loading user data
  if (isLoadingUser) {
    return (
      <div className="w-48 h-10 bg-gray-100 animate-pulse rounded-lg"></div>
    );
  }

  // Don't render if no branches available
  if (branchOptions.length === 0) {
    return null;
  }

  return (
    <SearchableSelect
      options={branchOptions}
      value={branchFilter === "all" ? "all" : branchFilter}
      onValueChange={handleBranchChange}
      placeholder="Sucursales"
      searchPlaceholder="Buscar sucursales..."
      allOptionLabel="Todas las sucursales"
      showAllOption={true}
      width="w-full"
      noResultsText="No se encontraron sucursales"
      searchPromptText="Escribe para buscar..."
      multiple={false}
    />
  );
}
